import type { Page } from '../../app/feature';
import type { ApiMission, MissionKind, MissionMode } from '../../shared/types';
import { delegate, esc, inputValue, q, qa, setInputValue } from '../../shared/dom';
import { getLang, t } from '../../shared/i18n';
import { money } from '../../shared/format';
import { formatMoney, parseMoney, refreshMoneyInputs } from '../../shared/ui/money-input';
import { ApiError } from '../../shared/api/http';
import { getState } from '../../shared/store/store';
import { hydrateFromApi } from '../../shared/store/sync';
import { toast } from '../../shared/ui/toast';
import { href, navigate } from '../../app/router';
import { missionsApi, type MissionTierInput } from './missions.api';

/* ---------------------------------------------------------------------------------
   Modelo da tela: o admin responde três perguntas em ordem
     1. O que o jogador faz?        → evento (depósito, aposta…) + como medir (valor/quantidade)
     2. Quantas cartelas ganha?     → proporcional, faixas, a cada N, ou fixo por evento
     3. Como mostramos ao jogador?  → nome ES/PT e descrição
   e vê um exemplo calculado ao vivo antes de salvar.
   As respostas são traduzidas para MissionKind + mode + faixas, que é o que a API guarda.
   --------------------------------------------------------------------------------- */

/** O que o jogador faz na casa (a pergunta 1 da tela). */
type EventChoice = 'DEPOSIT' | 'BET_CASINO' | 'BET_SPORTS' | 'BET_ANY' | 'TOURNAMENT' | 'REFERRAL' | 'MANUAL';
/** Como medir um depósito ou aposta: pelo valor ou pela quantidade de eventos. */
type Basis = 'VALUE' | 'COUNT';

const EVENTS: { id: EventChoice; icon: string; measurable: boolean }[] = [
  { id: 'DEPOSIT', icon: '💳', measurable: true },
  { id: 'BET_CASINO', icon: '🎰', measurable: true },
  { id: 'BET_SPORTS', icon: '⚽', measurable: true },
  { id: 'BET_ANY', icon: '🎯', measurable: true },
  { id: 'TOURNAMENT', icon: '🏆', measurable: false },
  { id: 'MANUAL', icon: '📸', measurable: false },
];

const KIND_OF: Record<EventChoice, Partial<Record<Basis, MissionKind>> & { UNIT?: MissionKind }> = {
  DEPOSIT:    { VALUE: 'DEPOSIT_AMOUNT', COUNT: 'DEPOSIT_COUNT' },
  BET_CASINO: { VALUE: 'BET_CASINO_AMOUNT', COUNT: 'BET_CASINO_COUNT' },
  BET_SPORTS: { VALUE: 'BET_SPORTS_AMOUNT', COUNT: 'BET_SPORTS_COUNT' },
  BET_ANY:    { VALUE: 'BET_AMOUNT', COUNT: 'BET_COUNT' },
  TOURNAMENT: { UNIT: 'TOURNAMENT' },
  REFERRAL:   { UNIT: 'REFERRAL' }, // legado: não é mais oferecido no formulário
  MANUAL:     { UNIT: 'MANUAL' },
};

/** Volta de MissionKind para (evento, medida). */
function splitKind(kind: MissionKind): { event: EventChoice; basis: Basis | 'UNIT' } {
  for (const [event, map] of Object.entries(KIND_OF) as [EventChoice, (typeof KIND_OF)[EventChoice]][]) {
    for (const [basis, k] of Object.entries(map) as [Basis | 'UNIT', MissionKind][]) if (k === kind) return { event, basis };
  }
  return { event: 'DEPOSIT', basis: 'VALUE' };
}

const isValueKind = (k: MissionKind): boolean => k.endsWith('_AMOUNT');
const isCountKind = (k: MissionKind): boolean => k.endsWith('_COUNT');

let editingId: string | null = null;
let keyTouched = false;

export const missionLabel = (m: ApiMission): string => (getLang() === 'pt' ? m.labelPt : m.labelEs) || m.labelEs;
export const kindLabel = (k: MissionKind): string => t('kind.' + k);

/** Frase curta da regra, para tabelas: "1 por cada $200 (máx. 50)", "≥ $5.000 → 1 · …", "cada 5 → 3". */
export function tiersSummary(m: ApiMission): string {
  if (isValueKind(m.kind) && m.mode === 'PER_AMOUNT') {
    return `1 ${t('mis.perEvery')} ${money(m.unitValue ?? 0)}${m.maxPerEvent ? ` (${t('mis.max')} ${m.maxPerEvent})` : ''}`;
  }
  if (isValueKind(m.kind)) return m.tiers.map(x => `≥ ${money(x.threshold)} → ${x.qty}`).join(' · ');
  if (isCountKind(m.kind)) return m.tiers.map(x => `${t('mis.every')} ${x.threshold} → ${x.qty}`).join(' · ');
  return `${m.tiers[0]?.qty ?? 0} ${t('mis.perEvent')}`;
}

/** Preenche {qty}/{num}/{event}/{Event} e outras variáveis num template do dicionário. */
function fill(template: string, vars: Record<string, string | number | undefined>): string {
  let out = template;
  const qty = vars.qty;
  if (typeof qty === 'number') out = out.replace('{qty}', String(qty)).replace('{num}', plural(qty));
  const event = vars.event;
  if (typeof event === 'string') out = out.replace('{event}', event).replace('{Event}', cap(event));
  for (const [k, v] of Object.entries(vars)) {
    if (v !== undefined && k !== 'qty' && k !== 'event') out = out.replace(`{${k}}`, String(v));
  }
  return out;
}

/** Frase completa, em linguagem de gente: "Cada depósito vale 1 número por cada $200 (máximo 50 por evento)." */
export function ruleSentence(m: ApiMission): string {
  const { event } = splitKind(m.kind);
  const noun = t('evnoun.' + event);
  if (isValueKind(m.kind) && m.mode === 'PER_AMOUNT') {
    return fill(t('rule.perAmount'), { event: noun, unit: money(m.unitValue ?? 0) })
      + (m.maxPerEvent ? ' ' + fill(t('rule.cap'), { max: m.maxPerEvent }) : '') + '.';
  }
  if (isValueKind(m.kind)) {
    const parts = m.tiers.map(x => fill(t('rule.tier'), { from: money(x.threshold), qty: x.qty }));
    return fill(t('rule.tiers'), { event: noun, list: parts.join('; ') }) + '.';
  }
  if (isCountKind(m.kind)) {
    const x = m.tiers[0];
    return fill(t('rule.count'), { n: x?.threshold ?? 0, event: t('evplural.' + event), qty: x?.qty ?? 0 }) + '.';
  }
  if (event === 'MANUAL') return fill(t('rule.manual'), { qty: m.tiers[0]?.qty ?? 0 }) + '.';
  return fill(t('rule.unit'), { event: noun, qty: m.tiers[0]?.qty ?? 0 }) + '.';
}

/**
 * Missões: o admin define, evento por evento, quantas cartelas cada ação do jogador vale.
 * A tela é um passo a passo com exemplo ao vivo; a API recebe kind + modo + faixas.
 */
export const missionsView: Page = {
  id: 'misiones',
  pattern: '/misiones',
  roles: ['ADMIN'],
  nav: { key: 'nav.missions', icon: '🎯', group: 'config' },

  template: () => `
<div>
  <div class="view-head">
    <div>
      <h2 id="mis-page-title" data-i18n="mis.newTitle">Nueva misión</h2>
      <p data-i18n="mis.sub2">Una misión dice cuántos números de la suerte gana el jugador por cada cosa que hace en la casa. Tú decides la regla de cada evento y puedes cambiarla cuando quieras: vale para lo que pase de ahí en adelante.</p>
    </div>
    <div class="spacer"></div>
    <a class="btn" href="${href('/misiones/configuradas')}" data-i18n="mis.seeList">Ver misiones configuradas</a>
  </div>
  ${missionsSubnav('new')}

  <div class="cols">
  <div class="span-8">
  <div class="panel">
    <div class="panel-head"><h3 id="mis-form-title" data-i18n="mis.new">Nueva misión</h3><div class="spacer"></div><button class="btn btn-sm btn-ghost" id="mis-cancel" hidden data-i18n="common.cancel">Cancelar</button></div>
    <form id="mis-form" class="stack" autocomplete="off" novalidate style="gap:22px">

      <!-- Paso 1: ¿qué hace el jugador? -->
      <section class="mis-step">
        <div class="mis-step-head"><span class="mis-num">1</span><div><h4 data-i18n="step1.title">¿Qué hace el jugador?</h4><p data-i18n="step1.lead">Elige el evento de la casa que va a generar números.</p></div></div>
        <div class="choice-grid" id="m-events">
          ${EVENTS.map(e => `<label class="choice"><input type="radio" name="m-event" value="${e.id}"><i>${e.icon}</i><b data-i18n="evname.${e.id}">${e.id}</b><span data-i18n="evdesc.${e.id}"></span></label>`).join('')}
        </div>
        <div class="row" id="m-basis-row" style="align-items:center; gap:14px">
          <span class="hint" style="flex:none" data-i18n="step1.basis">¿Cómo lo medimos?</span>
          <label class="choice choice-inline"><input type="radio" name="m-basis" value="VALUE" checked><b data-i18n="basis.VALUE">Por el monto</b><span data-i18n="basis.VALUEdesc">Cuanto más alto, más números</span></label>
          <label class="choice choice-inline"><input type="radio" name="m-basis" value="COUNT"><b data-i18n="basis.COUNT">Por la cantidad de veces</b><span data-i18n="basis.COUNTdesc">Cada N veces, un extra</span></label>
        </div>
      </section>

      <!-- Paso 2: ¿cuántas cartelas gana? -->
      <section class="mis-step">
        <div class="mis-step-head"><span class="mis-num">2</span><div><h4 data-i18n="step2.title">¿Cuántos números gana?</h4><p id="step2-lead"></p></div></div>

        <div id="m-value-block">
          <div class="choice-grid choice-grid-2">
            <label class="choice"><input type="radio" name="m-mode" value="PER_AMOUNT" checked><i>➗</i><b data-i18n="mode.PER_AMOUNT">Proporcional al monto</b><span data-i18n="mode.PER_AMOUNTdesc">Define cuántos pesos vale un número. $1.000 con $200 por número = 5 números.</span></label>
            <label class="choice"><input type="radio" name="m-mode" value="TIERS"><i>🪜</i><b data-i18n="mode.TIERS">Por tramos fijos</b><span data-i18n="mode.TIERSdesc">Desde tal monto vale tantos números. Se aplica el tramo más alto alcanzado.</span></label>
          </div>
          <div class="row" id="m-unit-row" style="margin-top:12px">
            <div class="field" style="flex:1 1 180px"><label for="m-unit" data-i18n="mis.unitValue">Pesos por número</label><input type="text" inputmode="numeric" class="money" id="m-unit" placeholder="$200"></div>
            <div class="field" style="flex:1 1 180px"><label for="m-max" data-i18n="mis.maxPerEvent">Máximo por evento (opcional)</label><input type="number" id="m-max" min="1" placeholder="50"></div>
            <div class="hint" style="flex:2 1 240px; align-self:end" data-i18n="mis.hintPerAmount2">Ejemplo: con $200 por número, un depósito de $1.000 vale 5 y uno de $3.500 vale 17 (se redondea hacia abajo). El máximo evita que un depósito enorme genere cientos de números.</div>
          </div>
          <div id="m-tiers-block" style="margin-top:12px">
            <div class="panel-head" style="margin-bottom:8px"><h3 style="font-size:12px" data-i18n="mis.tiers">Tramos</h3><div class="spacer"></div><button type="button" class="btn btn-sm" id="m-add-tier">+ <span data-i18n="mis.addTier">Agregar tramo</span></button></div>
            <p class="hint" data-i18n="mis.hintValue2">Cada fila dice: desde este monto, el evento vale tantos números. Un depósito de $30.000 con tramos $5.000→1 y $20.000→3 vale 3 (el tramo más alto que alcanzó).</p>
            <div class="stack" id="m-tiers"></div>
          </div>
        </div>

        <div id="m-count-block" class="row" style="align-items:end">
          <div class="field" style="flex:0 1 160px"><label for="m-step" data-i18n="mis.every2">Cada cuántas veces</label><input type="number" id="m-step" min="1" value="5"></div>
          <div class="field" style="flex:0 1 160px"><label for="m-count-qty" data-i18n="mis.qtyWin">Números que gana</label><input type="number" id="m-count-qty" min="1" max="100" value="3"></div>
          <div class="hint" style="flex:2 1 240px" data-i18n="mis.hintCount2">Se cuentan todos los eventos de ese tipo del jugador. Al llegar a la 5.ª, 10.ª, 15.ª vez… gana el extra.</div>
        </div>

        <div id="m-unit-block" class="row" style="align-items:end">
          <div class="field" style="flex:0 1 160px"><label for="m-unit-qty" data-i18n="mis.qtyWin">Números que gana</label><input type="number" id="m-unit-qty" min="1" max="100" value="1"></div>
          <div class="hint" style="flex:2 1 240px" id="m-unit-hint"></div>
        </div>

        <div class="mis-preview" id="m-preview"></div>
      </section>

      <!-- Paso 3: ¿cómo se lo mostramos al jugador? -->
      <section class="mis-step">
        <div class="mis-step-head"><span class="mis-num">3</span><div><h4 data-i18n="step3.title">¿Cómo se lo mostramos al jugador?</h4><p data-i18n="step3.lead">Este texto aparece en la página de misiones del sitio. Escríbelo como se lo explicarías a él.</p></div></div>
        <div class="row">
          <div class="field" style="flex:1 1 220px"><label for="m-es" data-i18n="mis.labelEs">Nombre (español)</label><input type="text" id="m-es" placeholder="Depósito"></div>
          <div class="field" style="flex:1 1 220px"><label for="m-pt" data-i18n="mis.labelPt">Nome (português)</label><input type="text" id="m-pt" placeholder="Depósito"></div>
        </div>
        <div class="field"><label for="m-desc" data-i18n="mis.desc2">Descripción para el jugador (español)</label><input type="text" id="m-desc" placeholder="Cada depósito pagado suma números según el monto."></div>
        <details class="mis-advanced"><summary data-i18n="mis.advanced">Avanzado: identificador técnico</summary>
          <div class="field" style="margin-top:10px; max-width:320px"><label for="m-key" data-i18n="mis.key">Clave</label><input type="text" id="m-key" placeholder="deposit" pattern="[a-z][a-z0-9_]*"><span class="hint" data-i18n="mis.keyHint">Se genera solo a partir del nombre. Sólo minúsculas, números y guion bajo; no cambia después de crear.</span></div>
        </details>
      </section>

      <div class="inline" style="gap:14px">
        <button type="submit" class="btn btn-primary" id="m-submit" style="padding:11px 22px" data-i18n="mis.save">Guardar misión</button>
        <span class="auth-error" id="m-error" role="alert" style="flex:1"></span>
      </div>
    </form>
  </div>
  </div>

  <aside class="span-4">
  <div class="grid mis-how sticky">
    <div class="mis-how-card"><i>💰</i><div><b data-i18n="how.value">Por monto</b><span data-i18n="how.valueDesc">Depósitos y apuestas: cuanto más alto el monto, más números. Proporcional (1 cada $200) o por tramos (desde $5.000 vale 1).</span></div></div>
    <div class="mis-how-card"><i>🔁</i><div><b data-i18n="how.count">Por cantidad</b><span data-i18n="how.countDesc">Premia la constancia: cada N depósitos o apuestas, el jugador gana X números extra.</span></div></div>
    <div class="mis-how-card"><i>⭐</i><div><b data-i18n="how.unit">Por evento</b><span data-i18n="how.unitDesc">Torneos y misiones manuales: cada vez que ocurre, vale una cantidad fija.</span></div></div>
    <div class="mis-how-card mis-how-tip"><i>💡</i><div><b data-i18n="how.tip">Consejo</b><span data-i18n="how.tipDesc">Empieza con reglas simples y mira la tabla de abajo: los números emitidos por misión muestran qué está funcionando.</span></div></div>
  </div>
  </aside>
  </div>
</div>`,

  mount() {
    const form = q<HTMLFormElement>('#mis-form');
    form.addEventListener('change', e => {
      const el = e.target as HTMLElement;
      if (el.matches('input[name=m-event], input[name=m-basis], input[name=m-mode]')) { syncSteps(); refreshPreview(); }
    });
    form.addEventListener('input', e => {
      const el = e.target as HTMLElement;
      if (el.id === 'm-es' && !editingId && !keyTouched) setInputValue('#m-key', slugify(inputValue('#m-es')));
      if (el.id === 'm-key') keyTouched = true;
      refreshPreview();
    });
    q('#m-add-tier').onclick = () => { renderTiers([...readTiers(), { threshold: 0, qty: 1 }]); refreshPreview(); };
    delegate(q('#m-tiers'), 'click', '[data-del-tier]', el => {
      const tiers = readTiers(); tiers.splice(Number(el.dataset.delTier), 1);
      renderTiers(tiers.length ? tiers : [{ threshold: 0, qty: 1 }]); refreshPreview();
    });
    q('#mis-cancel').onclick = () => { resetForm(); navigate('/misiones', true); };

    form.addEventListener('submit', async e => {
      e.preventDefault();
      const error = q('#m-error'); error.textContent = '';
      const draft = readDraft();
      if (!draft.ok) { error.textContent = draft.error; return; }
      const submit = q<HTMLButtonElement>('#m-submit'); submit.disabled = true;
      try {
        if (editingId) await missionsApi.update(editingId, draft.payload);
        else await missionsApi.create({ key: draft.key, ...draft.payload });
        toast(t('msg.saved'));
        resetForm();
        await hydrateFromApi();
        navigate('/misiones/configuradas');
      } catch (err) {
        error.textContent = err instanceof ApiError ? err.message : t('auth.errGeneric');
      } finally {
        submit.disabled = false;
      }
    });

    resetForm();
  },

  render(_params, query) {
    /* `#/misiones?edit=<id>` abre a missão no formulário; sem `edit`, volta ao modo "nova" */
    const editId = query.edit ?? null;
    if (editId !== editingId) {
      const m = editId ? getState().missions.find(x => x.id === editId) : undefined;
      if (m) fillForm(m);
      else if (!editId) resetForm();
    }
    q('#mis-page-title').textContent = editingId ? t('mis.editTitle') : t('mis.newTitle');
    // textos das opções e o exemplo dependem do idioma
    syncSteps(); refreshPreview();
  }
};

/** Abas "Nueva misión | Configuradas" no topo das duas páginas. */
export function missionsSubnav(active: 'new' | 'list'): string {
  return `<nav class="subnav">
    <a class="${active === 'new' ? 'on' : ''}" href="${href('/misiones')}" data-i18n="mis.subNew">Nueva misión</a>
    <a class="${active === 'list' ? 'on' : ''}" href="${href('/misiones/configuradas')}" data-i18n="mis.subList">Configuradas</a>
  </nav>`;
}

/** Ícone do evento da casa que alimenta a missão. */
export function missionIcon(m: ApiMission): string {
  return EVENTS.find(e => e.id === splitKind(m.kind).event)?.icon ?? '•';
}

/** "Depósito · Por el monto" — o evento e como é medido. */
export function missionMeasure(m: ApiMission): string {
  const { event } = splitKind(m.kind);
  const measure = isValueKind(m.kind) ? ' · ' + t('basis.VALUE') : isCountKind(m.kind) ? ' · ' + t('basis.COUNT') : '';
  return t('evname.' + event) + measure;
}

/* ------------------------------ leitura do formulário ------------------------------ */

const checked = (name: string): string => q<HTMLInputElement>(`input[name=${name}]:checked`).value;
const currentEventChoice = (): EventChoice => checked('m-event') as EventChoice;
const currentBasis = (): Basis => checked('m-basis') as Basis;
const currentModeChoice = (): MissionMode => checked('m-mode') as MissionMode;

function currentKind(): MissionKind {
  const map = KIND_OF[currentEventChoice()];
  return (map.UNIT ?? map[currentBasis()]) as MissionKind;
}

interface DraftPayload {
  kind: MissionKind; labelEs: string; labelPt: string; descriptionEs?: string;
  mode: MissionMode; unitValue?: number; maxPerEvent?: number | null; tiers: MissionTierInput[];
}
type Draft = { ok: true; key: string; payload: DraftPayload } | { ok: false; error: string };

function readDraft(): Draft {
  const kind = currentKind();
  const labelEs = inputValue('#m-es'), labelPt = inputValue('#m-pt') || labelEs, descriptionEs = inputValue('#m-desc');
  const key = (inputValue('#m-key') || slugify(labelEs)).toLowerCase();
  if (labelEs.length < 2) return { ok: false, error: t('mis.errLabel') };
  if (!editingId && !/^[a-z][a-z0-9_]{1,39}$/.test(key)) return { ok: false, error: t('mis.errKey') };

  const base = { kind, labelEs, labelPt, descriptionEs: descriptionEs || undefined };
  if (isValueKind(kind)) {
    if (currentModeChoice() === 'PER_AMOUNT') {
      const unitValue = parseMoney(inputValue('#m-unit'));
      const maxRaw = parseInt(inputValue('#m-max'), 10);
      if (unitValue < 1) return { ok: false, error: t('mis.errUnit') };
      return { ok: true, key, payload: { ...base, mode: 'PER_AMOUNT', unitValue, maxPerEvent: maxRaw > 0 ? maxRaw : null, tiers: [] } };
    }
    const tiers = readTiers();
    if (!tiers.length || tiers.some(x => x.qty < 1)) return { ok: false, error: t('mis.errTiers') };
    return { ok: true, key, payload: { ...base, mode: 'TIERS', tiers } };
  }
  if (isCountKind(kind)) {
    const step = parseInt(inputValue('#m-step'), 10) || 0, qty = parseInt(inputValue('#m-count-qty'), 10) || 0;
    if (step < 1 || qty < 1) return { ok: false, error: t('mis.errTiers') };
    return { ok: true, key, payload: { ...base, mode: 'TIERS', tiers: [{ threshold: step, qty }] } };
  }
  const qty = parseInt(inputValue('#m-unit-qty'), 10) || 0;
  if (qty < 1) return { ok: false, error: t('mis.errTiers') };
  return { ok: true, key, payload: { ...base, mode: 'TIERS', tiers: [{ threshold: 0, qty }] } };
}

function readTiers(): MissionTierInput[] {
  return qa('#m-tiers .row').map(row => ({
    threshold: parseMoney(q<HTMLInputElement>('[data-k=threshold]', row).value),
    qty: parseInt(q<HTMLInputElement>('[data-k=qty]', row).value, 10) || 0,
  }));
}

function renderTiers(tiers: MissionTierInput[]): void {
  q('#m-tiers').innerHTML = tiers.map((x, i) => `
    <div class="row" style="align-items:end">
      <div class="field" style="flex:1 1 160px"><label>${esc(t('mis.from'))}</label><input type="text" inputmode="numeric" class="money" value="${formatMoney(x.threshold)}" placeholder="$5.000" data-k="threshold"></div>
      <div class="field" style="flex:1 1 120px"><label>${esc(t('mis.qtyWin'))}</label><input type="number" min="1" max="100" value="${x.qty}" data-k="qty"></div>
      ${tiers.length > 1 ? `<div class="field" style="flex:0 0 auto"><button type="button" class="btn btn-sm btn-ghost" data-del-tier="${i}" title="${esc(t('common.del'))}">✕</button></div>` : ''}
    </div>`).join('');
}

/* ------------------------------ estado visual dos passos ------------------------------ */

function syncSteps(): void {
  const ev = currentEventChoice();
  const measurable = EVENTS.find(e => e.id === ev)?.measurable ?? false;
  const kind = currentKind();
  qa('.choice').forEach(c => c.classList.toggle('on', (c.querySelector('input') as HTMLInputElement).checked));

  q('#m-basis-row').hidden = !measurable;
  q('#m-value-block').hidden = !isValueKind(kind);
  q('#m-count-block').hidden = !isCountKind(kind);
  q('#m-unit-block').hidden = isValueKind(kind) || isCountKind(kind);
  const proportional = isValueKind(kind) && currentModeChoice() === 'PER_AMOUNT';
  q('#m-unit-row').hidden = !proportional;
  q('#m-tiers-block').hidden = proportional;

  q('#step2-lead').textContent = isValueKind(kind) ? t('step2.value') : isCountKind(kind) ? t('step2.count') : t('step2.unit');
  q('#m-unit-hint').textContent = t('unithint.' + ev, t('unithint.default'));
}

/** Exemplo calculado ao vivo com os valores digitados. */
function refreshPreview(): void {
  const box = q('#m-preview');
  const kind = currentKind();
  const ev = currentEventChoice();
  const noun = t('evnoun.' + ev);
  const lines: string[] = [];

  if (isValueKind(kind)) {
    if (currentModeChoice() === 'PER_AMOUNT') {
      const unit = parseMoney(inputValue('#m-unit'));
      const max = parseInt(inputValue('#m-max'), 10) || 0;
      if (unit > 0) {
        for (const amount of sampleAmounts(unit)) {
          const raw = Math.floor(amount / unit);
          const n = max ? Math.min(raw, max) : raw;
          lines.push(`${cap(noun)} ${t('prev.of')} <b>${money(amount)}</b> → <b>${n}</b> ${plural(n)}${max && raw > max ? ` <span class="muted">(${esc(t('prev.capped'))})</span>` : ''}`);
        }
      }
    } else {
      const tiers = readTiers().filter(x => x.qty > 0).sort((a, b) => a.threshold - b.threshold);
      const first = tiers[0]?.threshold ?? 1000, top = tiers[tiers.length - 1]?.threshold ?? 0;
      const samples = [Math.max(1000, Math.floor(first / 2)), ...tiers.map(x => x.threshold), top * 2].filter((v, i, a) => v > 0 && a.indexOf(v) === i);
      for (const amount of samples) {
        const reached = tiers.filter(x => x.threshold <= amount).pop();
        const n = reached?.qty ?? 0;
        lines.push(`${cap(noun)} ${t('prev.of')} <b>${money(amount)}</b> → <b>${n}</b> ${plural(n)}`);
      }
    }
  } else if (isCountKind(kind)) {
    const step = parseInt(inputValue('#m-step'), 10) || 0, qty = parseInt(inputValue('#m-count-qty'), 10) || 0;
    if (step > 0) {
      lines.push(fill(t('prev.countLine'), { n: step, event: noun, qty }).replace(` ${qty} `, ` <b>${qty}</b> `));
      lines.push(fill(t('prev.countLine2'), { n: step * 2, qty: qty * 2 }).replace(` ${qty * 2} `, ` <b>${qty * 2}</b> `));
    }
  } else {
    const qty = parseInt(inputValue('#m-unit-qty'), 10) || 0;
    lines.push(cap(ev === 'MANUAL' ? fill(t('rule.manual'), { qty }) : fill(t('rule.unit'), { event: noun, qty })).replace(` ${qty} `, ` <b>${qty}</b> `));
  }

  box.innerHTML = lines.length
    ? `<div class="mis-preview-title">${esc(t('prev.title'))}</div><ul>${lines.map(l => `<li>${l}</li>`).join('')}</ul>`
    : `<div class="mis-preview-title">${esc(t('prev.title'))}</div><p class="hint">${esc(t('prev.empty'))}</p>`;
}

function sampleAmounts(unit: number): number[] {
  const base = [1000, 5000, 20000, 100000];
  return [...new Set([unit, ...base.filter(v => v >= unit)])].slice(0, 4);
}

const plural = (n: number): string => (n === 1 ? t('prev.one') : t('prev.many'));
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/* ------------------------------ preencher / limpar ------------------------------ */

function setRadio(name: string, value: string): void {
  q<HTMLInputElement>(`input[name=${name}][value="${value}"]`).checked = true;
}

function fillForm(m: ApiMission): void {
  editingId = m.id; keyTouched = true;
  const { event, basis } = splitKind(m.kind);
  q('#mis-form-title').textContent = `${t('common.edit')}: ${missionLabel(m)}`;
  refreshMoneyInputs();
  q('#mis-cancel').hidden = false;
  setRadio('m-event', event);
  if (basis !== 'UNIT') setRadio('m-basis', basis);
  setRadio('m-mode', m.mode);
  setInputValue('#m-unit', m.unitValue ? formatMoney(m.unitValue) : ''); setInputValue('#m-max', m.maxPerEvent ? String(m.maxPerEvent) : '');
  if (isCountKind(m.kind)) { setInputValue('#m-step', String(m.tiers[0]?.threshold ?? 5)); setInputValue('#m-count-qty', String(m.tiers[0]?.qty ?? 1)); }
  if (!isValueKind(m.kind) && !isCountKind(m.kind)) setInputValue('#m-unit-qty', String(m.tiers[0]?.qty ?? 1));
  renderTiers(isValueKind(m.kind) && m.tiers.length ? m.tiers.map(x => ({ threshold: x.threshold, qty: x.qty })) : [{ threshold: 5000, qty: 1 }]);
  setInputValue('#m-es', m.labelEs); setInputValue('#m-pt', m.labelPt); setInputValue('#m-desc', m.descriptionEs ?? '');
  setInputValue('#m-key', m.key); q<HTMLInputElement>('#m-key').disabled = true;
  syncSteps(); refreshPreview();
  q('#mis-form').scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function resetForm(): void {
  editingId = null; keyTouched = false;
  q('#mis-form-title').textContent = t('mis.new');
  q('#mis-cancel').hidden = true;
  q<HTMLFormElement>('#mis-form').reset();
  setRadio('m-event', 'DEPOSIT'); setRadio('m-basis', 'VALUE'); setRadio('m-mode', 'PER_AMOUNT');
  setInputValue('#m-unit', formatMoney(200)); setInputValue('#m-max', '');
  setInputValue('#m-step', '5'); setInputValue('#m-count-qty', '3'); setInputValue('#m-unit-qty', '1');
  q<HTMLInputElement>('#m-key').disabled = false;
  renderTiers([{ threshold: 5000, qty: 1 }, { threshold: 20000, qty: 3 }]);
  syncSteps(); refreshPreview();
}

/** 'Depósito VIP' → 'deposito_vip' */
function slugify(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^[^a-z]+/, '').slice(0, 40);
}
